import React, { useState, useEffect, useRef } from 'react';
import { getMediaFilePath } from 'backend/additionalInformation';
import { TLoaderType, TPhotoSize, RequestEntity } from 'types';
import LoaderWithProps from 'components/Loader/LoaderWithProps';
import breadcrumbs from 'images/breadcrumbs.svg';
import classNames from 'classnames';
import style from './VideoUploaderByEntityId.module.scss';

interface IVideoUploaderForStyleSettings {
	resource: RequestEntity;
	entityID: string; // if "" will show noImageIcon
	isMakeImageRequest?: boolean;
	loaderSize: TLoaderType;
	isShowControls: boolean;
	noImageIdIcon?: string;
	videoSize?: TPhotoSize;
	fullWidth?: boolean;
}

const VideoUploaderByEntityId: React.FC<IVideoUploaderForStyleSettings> = ({
	resource,
	entityID,
	isMakeImageRequest = true,
	loaderSize,
	isShowControls,
	noImageIdIcon = 'teaserDefaultAvatar',
	videoSize = 'Original',
	fullWidth = true,
}) => {
	const [isShowImage, setIsShowImage] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [actualPath, setActualPath] = useState<string>('');
	const componentMounted = useRef(true);
	const videoClasses = classNames(style.video, { [style.fullWidth]: !isShowControls || fullWidth });

	const initialization = async () => {
		try {
			const response = await getMediaFilePath({ resource, entityID, size: videoSize });
			if (componentMounted.current) {
				setActualPath(response.data.preSignedUrl || '');
			}
		} catch (err) {
			if (componentMounted.current) {
				setActualPath('error');
				setIsLoading(false);
			}
		}
	};

	useEffect(() => {
		return () => {
			!entityID && setActualPath('');
		};
	}, [entityID]);

	const handleError = () => {
		setActualPath('error');
		setIsLoading(false);
	};

	useEffect(() => {
		if (isShowImage) {
			entityID !== '' ? initialization() : setIsLoading(false);
		}
	}, [isShowImage]);

	// ----- This is to avoid unnecessary photo requests ----- //
	useEffect(() => {
		if (!isShowImage && isMakeImageRequest) {
			setIsShowImage(true);
		}
	}, [isMakeImageRequest]);
	// ----- This is to avoid unnecessary photo requests ----- //

	useEffect(() => {
		return () => {
			componentMounted.current = false;
		};
	}, []);

	return (
		<div className={style.container} data-test="VideoUploaderByEntityId">
			{isLoading ? <LoaderWithProps loaderSize={loaderSize} /> : null}
			{actualPath !== 'error' && actualPath ? (
				<video
					className={videoClasses}
					controls={isShowControls}
					src={actualPath}
					onLoadedData={() => setIsLoading(false)}
					onError={handleError}
				/>
			) : null}
			{actualPath === '' ? (
				<div className={style.noImage}>
					<svg className={style.icon}>
						<use xlinkHref={`${breadcrumbs}#${noImageIdIcon}`} />
					</svg>
				</div>
			) : null}
			{actualPath === 'error' ? (
				<div className={style.noImage}>
					<div className={style.flexColumn}>
						<svg className={style.iconError}>
							<use xlinkHref={`${breadcrumbs}#brokenPicture`} />
						</svg>
						<div>Error video</div>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default React.memo(VideoUploaderByEntityId);
